import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell } from 'lucide-react';
import { Badge } from './ui/badge';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 60_000;  // refresh badge every 60s

/**
 * Bell icon in the navbar. Polls /api/notifications/unread-count every 60s
 * and exposes a dropdown with the latest 10 notifications + "mark all read".
 */
export const NotificationBell = () => {
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const intervalRef = useRef(null);

  const refreshCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/notifications/unread-count`, { withCredentials: true });
      setUnread(res.data?.unread_count || 0);
    } catch {
      // 401 → not authenticated → silently hide the badge
      setUnread(0);
    }
  }, []);

  const refreshList = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await axios.get(`${API_URL}/api/notifications`, {
        params: { limit: 10 },
        withCredentials: true,
      });
      setItems(res.data?.notifications || []);
    } catch {
      setItems([]);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    refreshCount();
    intervalRef.current = setInterval(refreshCount, POLL_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [refreshCount]);

  // Opening the dropdown fetches the full list lazily.
  useEffect(() => {
    if (open) refreshList();
  }, [open, refreshList]);

  const handleItemClick = async (item) => {
    try {
      await axios.post(
        `${API_URL}/api/notifications/${item.notification_id}/mark-read`,
        {},
        { withCredentials: true }
      );
    } catch { /* ignore — UI updates anyway */ }
    setOpen(false);
    refreshCount();
    if (item.link) navigate(item.link);
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API_URL}/api/notifications/mark-all-read`, {}, { withCredentials: true });
      setUnread(0);
      refreshList();
    } catch { /* ignore */ }
  };

  const fmtTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const diffMin = (Date.now() - d.getTime()) / 60000;
    if (diffMin < 1) return 'ahora';
    if (diffMin < 60) return `hace ${Math.round(diffMin)}m`;
    if (diffMin < 24 * 60) return `hace ${Math.round(diffMin / 60)}h`;
    return d.toLocaleDateString();
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-muted transition-colors"
          aria-label="Notificaciones"
          data-testid="notification-bell-btn"
        >
          <Bell className="w-5 h-5 text-foreground" strokeWidth={1.8} />
          {unread > 0 && (
            <Badge
              className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 text-[10px] bg-red-500 text-white border-white rounded-full flex items-center justify-center"
              data-testid="notification-bell-badge"
            >
              {unread > 9 ? '9+' : unread}
            </Badge>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0" data-testid="notification-dropdown">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <p className="font-medium text-sm">Notificaciones</p>
          {unread > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-[11px]"
              onClick={markAllRead}
              data-testid="notification-mark-all-read"
            >
              Marcar todas como leídas
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {loadingList ? (
            <p className="px-3 py-6 text-xs text-center text-muted-foreground">Cargando...</p>
          ) : items.length === 0 ? (
            <p className="px-3 py-6 text-xs text-center text-muted-foreground italic">
              Aún no hay notificaciones.
            </p>
          ) : (
            items.map((item) => (
              <button
                key={item.notification_id}
                onClick={() => handleItemClick(item)}
                className={`w-full text-left px-3 py-2 border-b last:border-b-0 hover:bg-muted/50 transition-colors ${item.read_at ? '' : 'bg-blue-50/40'}`}
                data-testid={`notification-item-${item.notification_id}`}
              >
                <div className="flex items-start gap-2">
                  {!item.read_at && (
                    <span className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium leading-tight">{item.title}</p>
                    {item.detail && (
                      <p className="text-[11px] text-muted-foreground mt-0.5 leading-tight truncate">{item.detail}</p>
                    )}
                    <p className="text-[10px] text-muted-foreground mt-1">{fmtTime(item.created_at)}</p>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
